// src/data/hearingRecord.js
//
// T5 / D-12 (2026-10-15) - THE HEARING RECORD: what happened ON the record at the hearing. The hearing log
// (DisciplineCases.js) holds date, place, officer, attendance, plea, witnesses and transcript notes - none of the
// things an appeal turns on. An objection not raised at the hearing is usually lost on appeal, so the platform has to
// hold the objection, the hearing officer's ruling on it, the testimony points, the exhibits and the recesses.
// Catalogue: DISC-hearing-conduct, DISC-hearing-conduct-cross-examination, DISC-right-to-hearing
// (data\YCP_VIOLATION_RULE_MAP.csv).
//
// Entries are appended to the case as hearingRecord[] (arrayUnion) by an officer of the case - never rewritten; a
// correction is a later entry that says what it corrects. Pure: no React, no Firestore.

const F = Object.freeze;
const str = (v) => (v == null ? '' : String(v)).trim();
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(str(v));

export const ENTRY_KINDS = F({
  objection:   F({ label: 'Objection made on the record',          needs: ['text'] }),
  ruling:      F({ label: 'Hearing officer\'s ruling on an objection', needs: ['text', 'ruling', 'onObjection'] }),
  testimony:   F({ label: 'Testimony point',                       needs: ['text', 'witness'] }),
  cross:       F({ label: 'Cross-examination point',               needs: ['text', 'witness'] }),
  exhibit:     F({ label: 'Exhibit introduced',                    needs: ['text', 'exhibitNo', 'offeredBy'] }),
  recess:      F({ label: 'Recess',                                needs: ['text'] }),
  correction:  F({ label: 'Correction to an earlier entry',        needs: ['text', 'corrects'] }),
});
export const KINDS = F(Object.keys(ENTRY_KINDS));
export const RULINGS = F(['sustained', 'overruled', 'reserved', 'not_ruled']);
export const RULING_LABELS = F({ sustained: 'Sustained', overruled: 'Overruled', reserved: 'Ruling reserved', not_ruled: 'No ruling made' });
export const OFFERED_BY = F(['carrier', 'organization']);
export const TEXT_MAX = 2000;

// Validate an entry. seq = the next sequence number (entries are numbered so a ruling or correction can point at one).
export function planEntry(input, seq) {
  const i = input || {};
  const kind = str(i.kind);
  const spec = ENTRY_KINDS[kind];
  if (!spec) return { ok: false, errors: ['"' + kind + '" is not a hearing record entry.'], entry: null };
  const errors = [];
  if (!isDate(i.on)) errors.push('The entry carries the hearing date, as YYYY-MM-DD.');
  const text = str(i.text);
  if (!text) errors.push('Write what was said or done.');
  else if (text.length > TEXT_MAX) errors.push('The entry is longer than ' + TEXT_MAX + ' characters.');
  const e = { seq: Number.isFinite(seq) ? seq : 1, kind, on: str(i.on), time: str(i.time) || null, text, pageRef: str(i.pageRef) || null };
  if (kind === 'ruling') {
    e.ruling = str(i.ruling);
    if (RULINGS.indexOf(e.ruling) === -1) errors.push('A ruling is one of: ' + RULINGS.join(', ') + '.');
    e.onObjection = Number(i.onObjection);
    if (!Number.isFinite(e.onObjection) || e.onObjection < 1) errors.push('Name the objection (its entry number) this ruling is on.');
  }
  if (kind === 'testimony' || kind === 'cross') { e.witness = str(i.witness); if (!e.witness) errors.push('Name the witness.'); }
  if (kind === 'exhibit') {
    e.exhibitNo = str(i.exhibitNo); if (!e.exhibitNo) errors.push('Give the exhibit its number or letter.');
    e.offeredBy = str(i.offeredBy); if (OFFERED_BY.indexOf(e.offeredBy) === -1) errors.push('Say which side offered the exhibit.');
    e.objectedTo = i.objectedTo === true;
  }
  if (kind === 'correction') {
    e.corrects = Number(i.corrects);
    if (!Number.isFinite(e.corrects) || e.corrects < 1) errors.push('Name the entry number this corrects.');
  }
  return { ok: errors.length === 0, errors, entry: errors.length ? null : e };
}

export function nextSeq(entries) {
  return (Array.isArray(entries) ? entries : []).reduce((m, e) => Math.max(m, Number(e && e.seq) || 0), 0) + 1;
}

// For the appeal: every objection with the ruling on it (the latest ruling that names it), and the ones never ruled on.
export function objectionsWithRulings(entries) {
  const list = (Array.isArray(entries) ? entries : []).filter(Boolean).slice().sort((a, b) => (a.seq || 0) - (b.seq || 0));
  return list.filter((e) => e.kind === 'objection').map((o) => {
    const rs = list.filter((r) => r.kind === 'ruling' && r.onObjection === o.seq);
    const r = rs.length ? rs[rs.length - 1] : null;
    return { objection: o, ruling: r ? r.ruling : null, rulingEntry: r, unruled: !r || r.ruling === 'not_ruled' || r.ruling === 'reserved' };
  });
}
